import { crypto } from '../environment/crypto/index.js';

/**
 * The previous `generateID` implementation generated IDs of varying length,
 * generally 18-20 characters long. This value remains unchanges as there
 * are still short IDs in the database.
 */

const MIN_ID_LENGTH = 17;
/**
 * Generates a unique ID, inspired by firestore’s implementation (`newID`).
 * IDs are 20 chars long by default.
 *
 * https://github.com/firebase/firebase-js-sdk/blob/firebase%409.18.0/packages/firestore/src/util/misc.ts
 */

const generateID = (targetLength = 20) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'; // The largest byte value that is a multiple of `char.length`.

  const maxMultiple = Math.floor(256 / chars.length) * chars.length;
  let autoId = '';

  while (autoId.length < targetLength) {
    const bytes = crypto.randomBytes(40);

    for (let i = 0; i < bytes.length; ++i) {
      // Eslint disabled to use firebase’s implementation.

      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      if (autoId.length < targetLength && bytes[i] < maxMultiple) {
        autoId += chars.charAt(bytes[i] % chars.length);
      }
      /* eslint-enable @typescript-eslint/no-non-null-assertion */

    }
  }

  return autoId;
};
/**
 * Generates a sortable ID, functionally inspired by Firebase RTDB push IDs
 * but also works in reverse chronological order, e.g. descending order.
 */

const generateSortableID = (order = 'ascending') => {
  const now = Date.now(); // Invert timestamp for descending order: Use a future timestamp as a base (e.g., year 3000)

  const futureTimestamp = new Date('3000-01-01').getTime();
  const adjustedTimestamp = order === 'descending' ? futureTimestamp - now : now; // Convert adjusted timestamp to a base-36 string

  const timestampPart = adjustedTimestamp.toString(36); // Generate a random alphanumeric string for the remaining characters

  const remainingLength = 20 - timestampPart.length;
  let randomPart = '';
  const possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < remainingLength; i += 1) {
    randomPart += possibleChars.charAt(Math.floor(Math.random() * possibleChars.length));
  } // Concatenate parts to form the ID


  return timestampPart + randomPart;
};
/** Generates a 28 char ID, similar to the ones generated by Firebase Auth */

const generateUserID = () => generateID(28);

export { MIN_ID_LENGTH, generateID, generateSortableID, generateUserID };
